import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import { WebFormService } from '../../services/WebFormService';
import { useSelector } from 'react-redux';
import * as selector from '../../selectors/selectors';
import GridComponent from '../shared/grid';
import { ColDef, ColumnApi, GridApi, ValueGetterParams } from 'ag-grid-community';
import { emptyLabels, pageTitles } from '../shared/constants';
import { mapToWebFormHistoryModel, WebFormHistoryModel } from '../../models/web-form.model';
import { UtilityService } from '../../services/UtilityService';
import { dateComparator } from '../shared/grid/grid.services';
import { setBackHandler, setHeaderTitle, setRefreshHandler } from '../../helpers/header.helper';
import { AppRoutes } from '../routes/route.path';
import { CustomAttributeDefinitionModel, CustomAttributeType } from '../../models/custom-attribute.model';


const WebFormHistory = () => {
    const params = useParams();
    const navigate = useNavigate();

    const IsDarkMode: boolean = useSelector(selector.isDarkMode);
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice);
    const CustomAttributeFieldData: CustomAttributeDefinitionModel[] = useSelector(selector.customAttributeFields);


    const [columnApi, setColumnApi] = useState<ColumnApi | null>(null);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [data, setData] = useState<WebFormHistoryModel | undefined>();
    const [customColumnDefs, setCustomColumnDefs] = useState<ColDef[]>([]);

    const WebFormHistoryColoumnDefs: ColDef[] = useMemo(() => {
        return [
            {
                field: "PhoneNumber",
                headerName: "Number",
                valueGetter: (params: ValueGetterParams) => params?.data?.PhoneNumber,
                wrapText: true,
                autoHeight: true,
            },
            {
                field: "Name",
                headerName: "Name",
                valueGetter: (params: ValueGetterParams) => params?.data?.Name,
                wrapText: true,
                autoHeight: true,
            },
            {
                field: "Email",
                headerName: "Email",
                valueGetter: (params: ValueGetterParams) => params?.data?.Email,
                wrapText: true,
                autoHeight: true,
            },
            {
                field: "Groups",
                headerName: "Groups",
                valueGetter: (params: ValueGetterParams) => params?.data?.Groups?.join(","),
                wrapText: true,
                autoHeight: true,
            },
            ...customColumnDefs,
            {
                field: "CreatedDateTime",
                headerName: "Created Date",
                valueGetter: (params: ValueGetterParams) => UtilityService.convertDateToDay(params.data.CreatedDateTime).toString(),
                sort: !IsMobileDevice ? 'desc' : null,
                hide: IsMobileDevice,
                comparator: (valueA, valueB, nodeA, nodeB) => dateComparator(nodeA.data.CreatedDateTime, nodeB.data.CreatedDateTime),
                suppressSizeToFit: true
            },
        ]
    }, [customColumnDefs]);

    const getDynamicColumns = (contacts: any[]): ColDef[] => {
        const headers = new Set<string>();

        contacts?.forEach(contact => {
            contact.CustomAttributes?.forEach(x => {
                headers.add(x.name);
            });
        });

        return Array.from(headers).map(name => ({
            field: name,
            headerName: name,
            wrapText: true,
            autoHeight: true,
        }));
    };

    const rowData = useMemo(() => {
        return (data?.SubmissionContacts || []).map(contact => {
            const flatRow = { ...contact };
            contact.CustomAttributes?.forEach(x => {
                // Dynamically add new property to the row

                const row = CustomAttributeFieldData.find(y => y.Reference === x.reference)
                if (row !== null && row?.Type === CustomAttributeType.Dropdown) {
                    flatRow[x.name] = row?.OptionsDetail?.find(o => o.id === x.value)?.label ?? x.value
                }
                else {
                    flatRow[x.name] = x.value;
                }
            });
            return flatRow;
        });
    }, [data]);

    useLayoutEffect(() => {
        gridRefresh()

    }, []);

    useEffect(() => {
        setHeaderTitle(pageTitles.WEBFORM_HISTORY)
        setBackHandler(() => { navigate(AppRoutes.WEBFORM) })
        setRefreshHandler(() => gridRefresh())
    }, [])


    const gridRefresh = () => {
        const formId = params.formId;

        WebFormService.getWebFormHistoryByid(formId)
            .then((res) => {
                const resData = mapToWebFormHistoryModel(res.data);
                setData(resData);
                const dynamicCols = getDynamicColumns(resData?.SubmissionContacts || []);

                setCustomColumnDefs(dynamicCols);
            })
            .catch(() => { });
    }

    const onGridReady = (params: any) => {
        setGridApi(params.api);
        setColumnApi(params.columnApi);
    };

    return (
        <>
            <div className={`d-flex flex-direction h-p-100 o-hidden justify-between ${IsDarkMode ? 'bg-color-primary-background' : 'bg-color-report-background'}`}>

                <div className='d-flex justify-between h-p-20 align-center px-2'>
                    <div className='w-p-25 border-primary-divider h-p-75 bg-color-secondary br-3 my-auto d-flex flex-direction justify-center'>
                        <div className='h3-semibold ml-3'>{data?.Views}</div>
                        <div className='h5-regular ml-3'>Total Views</div>
                    </div>

                    <div className='w-p-25 border-primary-divider h-p-75 bg-color-secondary br-3 my-auto d-flex flex-direction justify-center'>
                        <div className='h3-semibold ml-3'>{data?.SubmissionsCount}</div>
                        <div className='h5-regular ml-3'>Total Submissions</div>
                    </div>

                    <div className='w-p-25 border-primary-divider h-p-75 bg-color-secondary br-3 my-auto d-flex flex-direction justify-center'>
                        <div className='h3-semibold ml-3'>{(data?.Views && data?.SubmissionsCount) ? ((data.SubmissionsCount / data.Views) * 100).toFixed(2) : "0"}%</div>
                        <div className='h5-regular ml-3'>Conversion rate</div>
                    </div>
                </div>

                <GridComponent
                    ColumnDefs={WebFormHistoryColoumnDefs}
                    RowData={rowData ?? []}
                    OnGridReady={onGridReady}
                    IsPaginationEnabled={!IsMobileDevice}
                    EmptyLabel={emptyLabels.WEBFORM_CONTACT_SUBMIT}
                    LockVisible={IsMobileDevice}
                    gridApi={gridApi}
                    isHorizontalScroll={true}
                />
            </div>
        </>
    );
};

export default WebFormHistory;